import { Capacitor } from '@capacitor/core';

/**
 * 系统级功能插件接口
 *
 * 提供应用级别的通用功能
 */
export interface SystemPlugin {
  /**
   * 使用系统浏览器打开链接（用于 App Store 等特殊链接）
   * @param options.url - 要打开的 URL
   */
  openSystemUrl(options: {
    url: string;
  }): Promise<{ success: boolean }>;

  /**
   * 获取应用缓存数据（用于调试）
   */
  getCacheData(): Promise<{
    api_base_url: string;
    has_token: boolean;
    user_id: string;
    target_user_id: string;
    freelancer_id: string;
    technician_type: string;
    chat_type: string;
  }>;

  /**
   * 清除应用缓存数据
   */
  clearCache(): Promise<{ success: boolean }>;

  /**
   * 获取应用安装来源（用于识别下载渠道所属的应用商店）
   */
  getInstallSource(): Promise<{
    installer: string | null;
    manufacturer: string | null;
  }>;
}

/**
 * 系统级功能插件实现
 */
export const SystemPlugin: SystemPlugin = {
  openSystemUrl: async (options) => {
    const { SystemPlugin: NativeSystemPlugin } = Capacitor.Plugins;
    if (NativeSystemPlugin) {
      return await NativeSystemPlugin.openSystemUrl(options);
    } else {
      console.warn('SystemPlugin not available in web environment');
      // Web 环境降级处理
      window.open(options.url, '_blank');
      return { success: true };
    }
  },

  getCacheData: async () => {
    const { SystemPlugin: NativeSystemPlugin } = Capacitor.Plugins;
    if (NativeSystemPlugin && NativeSystemPlugin.getCacheData) {
      return await NativeSystemPlugin.getCacheData();
    } else {
      console.warn('getCacheData not available in web environment');
      return {
        api_base_url: '',
        has_token: false,
        user_id: '',
        target_user_id: '',
        freelancer_id: '',
        technician_type: '',
        chat_type: ''
      };
    }
  },

  clearCache: async () => {
    const { SystemPlugin: NativeSystemPlugin } = Capacitor.Plugins;
    if (NativeSystemPlugin && NativeSystemPlugin.clearCache) {
      return await NativeSystemPlugin.clearCache();
    } else {
      console.warn('clearCache not available in web environment');
      // Web 环境清除 localStorage
      localStorage.clear();
      return { success: true };
    }
  },

  getInstallSource: async () => {
    const fallback = { installer: null, manufacturer: null };
    // 仅 Android 有该方法的 Native 实现（iOS 端 SystemPlugin 只注册了 openSystemUrl）
    if (Capacitor.getPlatform() !== 'android') {
      return fallback;
    }
    const { SystemPlugin: NativeSystemPlugin } = Capacitor.Plugins;
    if (NativeSystemPlugin && NativeSystemPlugin.getInstallSource) {
      return await NativeSystemPlugin.getInstallSource();
    } else {
      console.warn('getInstallSource not available in web environment');
      return fallback;
    }
  }
};
